import {
  Box,
  EmptyState,
  Icon,
  LegacyCard,
  LegacyStack,
  Loading,
  ResourceItem,
  ResourceList,
  Text,
  TextField,
} from '@shopify/polaris'
import { SearchIcon } from '@shopify/polaris-icons'
import React, { useEffect, useState } from 'react'

import { ShowIntercomLink, useDebounced } from '../common/helpers'
import { LoadErrorBanner } from '../common/LoadErrorPage'
import { TableSortBySelector } from '../common/TableSortBySelector'
import * as urls from '../common/urls'
import { freeSetupIntercomMessage } from '../getting-started/GettingStarted'
import { SellingPlanGroup, useSellingPlanGroupsSWR } from './api'
import styles from './SellingPlanGroupList.module.css'

const EmptySellingPlanGroupCard = (
  <div className={styles.wrapper}>
    <EmptyState
      heading="Create a new selling plan to get started"
      action={{ content: 'Create Plan', url: urls.newPlanUrl }}
      image="/images/setup-plans-small.svg"
    >
      <Text as="p">
        Or <ShowIntercomLink message={freeSetupIntercomMessage}>tell us</ShowIntercomLink> what you
        want to offer and our team of experts will setup your plans at no cost!
      </Text>
    </EmptyState>
  </div>
)

const Item = (group: SellingPlanGroup) => {
  return (
    <ResourceItem id={group.id!} url={urls.planUrl(group.id!)}>
      <h3>
        <Text as="span" fontWeight="semibold">
          {group.merchantCode}
        </Text>
      </h3>
      {group.summary}
    </ResourceItem>
  )
}

export const SellingPlanGroupList = () => {
  const [sortKey, setSortKey] = useState('CREATED_AT_DESC')
  const [beforeCursor, setBeforeCursor] = useState('')
  const [afterCursor, setAfterCursor] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounced(searchTerm, 500)

  useEffect(() => {
    setBeforeCursor('')
    setAfterCursor('')
  }, [debouncedSearchTerm, sortKey])

  const { data, error, isValidating } = useSellingPlanGroupsSWR(
    sortKey,
    beforeCursor,
    afterCursor,
    debouncedSearchTerm
  )
  const items = data?.nodes || []

  const sortOptions = [
    { label: 'Created (oldest first)', value: 'CREATED_AT_ASC' },
    { label: 'Created (newest first)', value: 'CREATED_AT_DESC' },
    { label: 'Updated (oldest first)', value: 'UPDATED_AT_ASC' },
    { label: 'Updated (newest first)', value: 'UPDATED_AT_DESC' },
  ]

  return (
    <>
      {isValidating && <Loading />}

      {error && <LoadErrorBanner />}
      {!error && (
        <LegacyCard>
          <ResourceList
            resourceName={{ singular: 'selling plan group', plural: 'selling plan groups' }}
            emptyState={!isValidating && debouncedSearchTerm ? null : EmptySellingPlanGroupCard}
            items={items}
            renderItem={Item}
            sortValue={sortKey}
            showHeader={true}
            loading={isValidating && !data}
            filterControl={
              <Box paddingBlockEnd="200">
                <LegacyStack alignment="center" spacing="tight">
                  <LegacyStack.Item fill>
                    <TextField
                      label=""
                      placeholder="Search selling plans"
                      value={searchTerm}
                      onChange={setSearchTerm}
                      clearButton
                      onClearButtonClick={() => setSearchTerm('')}
                      prefix={<Icon source={SearchIcon} />}
                      autoComplete="off"
                    />
                  </LegacyStack.Item>
                  <LegacyStack.Item>
                    <TableSortBySelector
                      sortBy={sortKey}
                      onChange={setSortKey}
                      choices={sortOptions}
                    />
                  </LegacyStack.Item>
                </LegacyStack>
              </Box>
            }
            pagination={
              items.length > 0
                ? {
                    hasPrevious: data?.page.hasPreviousPage,
                    hasNext: data?.page.hasNextPage,
                    onPrevious: () => {
                      setAfterCursor('')
                      setBeforeCursor(data?.page?.firstCursor || '')
                    },
                    onNext: () => {
                      setBeforeCursor('')
                      setAfterCursor(data?.page?.lastCursor || '')
                    },
                  }
                : undefined
            }
          />
        </LegacyCard>
      )}
    </>
  )
}
